// client/src/coms/serveradmin/adminKit.js
// Small building blocks shared by the Server Settings sections: data loading, error text, switches, setting
// rows, confirmation dialogs, chips and time formatting. Everything is rendered as text (no HTML from data).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { comsApiGet } from '../comsApi';

export function errorText(e, fallback = 'Something went wrong.') {
  return String((e && e.data && e.data.error) || fallback);
}

// GET a URL; { data, err, loading, reload, setData }.
export function useLoad(url, deps = []) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const alive = useRef(true);
  const reload = useCallback(async () => {
    if (!url) return null;
    setLoading(true);
    setErr('');
    try {
      const r = await comsApiGet(url);
      if (alive.current) setData(r);
      return r;
    } catch (e) {
      if (alive.current) setErr(errorText(e, 'Could not load this section.'));
      return null;
    } finally {
      if (alive.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, ...deps]);
  useEffect(() => {
    alive.current = true;
    reload();
    return () => {
      alive.current = false;
    };
  }, [reload]);
  return { data, err, loading, reload, setData };
}

// Runs an async action with busy / message state.
export function useAction() {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // { kind: 'ok' | 'error', text }
  const timer = useRef(null);
  const run = useCallback(async (fn, okText) => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await fn();
      if (okText) {
        setMsg({ kind: 'ok', text: okText });
        clearTimeout(timer.current);
        timer.current = setTimeout(() => setMsg(null), 3500);
      }
      return r;
    } catch (e) {
      setMsg({ kind: 'error', text: errorText(e) });
      return null;
    } finally {
      setBusy(false);
    }
  }, []);
  useEffect(() => () => clearTimeout(timer.current), []);
  return { busy, msg, run, setMsg };
}

export function Flash({ msg }) {
  if (!msg) return null;
  return (
    <div className={`vxa-flash${msg.kind === 'error' ? ' is-error' : msg.kind === 'warn' ? ' is-warn' : ''}`} role={msg.kind === 'error' ? 'alert' : 'status'}>
      {msg.text}
    </div>
  );
}

export function SectionHead({ title, children, actions }) {
  return (
    <div className="vxa-section-head">
      <div>
        <h2>{title}</h2>
        {children ? <p>{children}</p> : null}
      </div>
      {actions ? <div className="vxa-row">{actions}</div> : null}
    </div>
  );
}

export function Card({ title, sub, children, actions }) {
  return (
    <section className="vxa-card">
      {title || actions ? (
        <div className="vxa-row is-spread" style={{ marginBottom: sub ? 0 : 8 }}>
          {title ? <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800 }}>{title}</h3> : <span />}
          {actions ? <div className="vxa-row">{actions}</div> : null}
        </div>
      ) : null}
      {sub ? <p className="vxa-card-sub" style={{ marginTop: 4 }}>{sub}</p> : null}
      {children}
    </section>
  );
}

export function Empty({ icon = '✨', title, children }) {
  return (
    <div className="vxo-empty">
      <div className="vxo-empty-icon" aria-hidden="true">{icon}</div>
      <div className="vxo-empty-title">{title}</div>
      {children ? <div className="vxo-empty-body">{children}</div> : null}
    </div>
  );
}

export function Switch({ checked, onChange, disabled, label, hideLabel }) {
  return (
    <label className="vxa-switch">
      <input type="checkbox" checked={!!checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} aria-label={hideLabel ? label : undefined} />
      <span className="vxa-switch-track" aria-hidden="true" />
      {hideLabel ? null : <span>{label}</span>}
    </label>
  );
}

export function Setting({ title, desc, children }) {
  return (
    <div className="vxa-setting">
      <div className="vxa-setting-text">
        <div className="vxa-setting-title">{title}</div>
        {desc ? <div className="vxa-setting-desc">{desc}</div> : null}
      </div>
      <div className="vxa-setting-control">{children}</div>
    </div>
  );
}

export function NumberInput({ value, onChange, min = 0, max = 100000, label, suffix, disabled }) {
  return (
    <span className="vxa-row" style={{ gap: 4 }}>
      <input
        type="number"
        className="vxo-input vxa-num"
        value={value}
        min={min}
        max={max}
        aria-label={label}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value === '' ? '' : Math.max(min, Math.min(max, Number(e.target.value))))}
      />
      {suffix ? <span className="vxa-muted vxa-small">{suffix}</span> : null}
    </span>
  );
}

export function Chip({ children, kind, color, title }) {
  return (
    <span className={`vxa-chip${kind ? ` is-${kind}` : ''}`} title={title}>
      {color ? <span className="vxa-chip-dot" style={{ background: color }} aria-hidden="true" /> : null}
      {children}
    </span>
  );
}

export function PillToggle({ options, value, onChange, multi = false, label }) {
  const set = new Set(multi ? value || [] : [value]);
  return (
    <div className="vxa-pill-toggle" role="group" aria-label={label}>
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          aria-pressed={set.has(o.value)}
          onClick={() => {
            if (!multi) return onChange(o.value);
            const next = new Set(set);
            if (next.has(o.value)) next.delete(o.value);
            else next.add(o.value);
            return onChange(Array.from(next));
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// A modal confirmation. typed: text the user must type exactly to enable the confirm button.
export function ConfirmDialog({ title, children, confirmLabel = 'Confirm', danger, typed, busy, onConfirm, onCancel }) {
  const [text, setText] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    const prev = document.activeElement;
    if (ref.current) ref.current.focus();
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      try {
        if (prev && prev.focus) prev.focus();
      } catch {}
    };
  }, [busy, onCancel]);
  const ok = !typed || text.trim() === String(typed).trim();
  return (
    <div className="vxo-backdrop" onMouseDown={(e) => e.target === e.currentTarget && !busy && onCancel()}>
      <div className="vxo-dialog" role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} ref={ref}>
        <div className="vxo-dialog-head">
          <h2 className="vxo-dialog-title">{title}</h2>
        </div>
        <div className="vxo-dialog-body">
          {children}
          {typed ? (
            <label className="vxo-field" style={{ marginTop: 12 }}>
              Type <b>{typed}</b> to confirm
              <input value={text} onChange={(e) => setText(e.target.value)} autoComplete="off" />
            </label>
          ) : null}
        </div>
        <div className="vxo-dialog-foot">
          <button type="button" className="vx-btn" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className={`vx-btn ${danger ? 'danger' : 'primary'}`} onClick={onConfirm} disabled={busy || !ok}>
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function fmtDate(v) {
  if (!v) return '';
  const ms = typeof v === 'number' ? v : Date.parse(String(v).includes('T') ? v : `${String(v).replace(' ', 'T')}Z`);
  if (!ms) return String(v);
  return new Date(ms).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export function fmtAgo(v) {
  const ms = typeof v === 'number' ? v : Date.parse(String(v).includes('T') ? v : `${String(v).replace(' ', 'T')}Z`);
  if (!ms) return '';
  const s = Math.round((Date.now() - ms) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return `${Math.floor(s / 86400)} d ago`;
}

export function fmtDuration(ms) {
  const m = Math.round(Number(ms || 0) / 60000);
  if (!m) return '';
  if (m < 60) return `${m} min`;
  if (m < 1440) return `${Math.round(m / 60)} h`;
  return `${Math.round(m / 1440)} d`;
}

export const ACTION_LABELS = {
  warn: 'Warning',
  timeout: 'Timeout',
  timeout_removed: 'Timeout removed',
  kick: 'Kick',
  ban: 'Ban',
  unban: 'Unban',
  note: 'Note',
  messages_removed: 'Messages removed',
  channel_locked: 'Channel locked',
  channel_unlocked: 'Channel unlocked',
  slowmode: 'Slowmode',
  voice_muted: 'Voice mute',
  voice_unmuted: 'Voice unmute',
  roles_removed: 'Roles removed',
  verification_required: 'Verification required',
};

export const REPORT_REASONS = {
  spam: 'Spam or scam',
  harassment: 'Harassment',
  hate: 'Hate speech',
  sexual: 'Sexual content',
  violence: 'Violence or threats',
  self_harm: 'Self-harm',
  illegal: 'Illegal content',
  other: 'Something else',
};

export const DURATIONS = [
  [10, '10 minutes'],
  [60, '1 hour'],
  [360, '6 hours'],
  [1440, '1 day'],
  [10080, '1 week'],
];

export function keyOf(serverKey) {
  return encodeURIComponent(String(serverKey || ''));
}
